import { BrandJusIa, BrandJusSolucoes, Modal, Spinner } from '@farol-ds/react'
import cn from 'classnames'
import { useEffect, useState } from 'react'

import { BillingElements } from '../billing-elements'
import { CHECKOUT_MESSAGES, CheckoutMessagePayload } from '../types'
import { BillingCheckoutInline } from './checkout-inline'
import styles from './checkout-modal.module.scss'

export type BillingCheckoutBrand = 'jusbrasil' | 'jusia' | 'jusolucoes'

const BRAND_COMPONENTS = {
  jusbrasil: <Modal.HeaderBrand />,
  jusia: <BrandJusIa onlySymbol width={28} />,
  jusolucoes: <BrandJusSolucoes onlySymbol width={28} />,
}

function getTitle(payload?: CheckoutMessagePayload): string | undefined {
  const title = payload?.title
  return typeof title === 'string' ? title : undefined
}

function useCheckoutState(elementContext: BillingElements): {
  title: string | undefined
  isResultPage: boolean
} {
  const [title, setTitle] = useState<string | undefined>(undefined)
  const [isResultPage, setIsResultPage] = useState(false)

  useEffect(() => {
    setTitle(undefined)
    setIsResultPage(false)
    const unsubLoaded = elementContext.on(CHECKOUT_MESSAGES.LOADED, (payload) => {
      setTitle(getTitle(payload))
      setIsResultPage(false)
    })
    const clearTitle = () => setTitle(undefined)
    const unsubSuccess = elementContext.on(CHECKOUT_MESSAGES.PAYMENT_SUCCESS, () => {
      clearTitle()
      setIsResultPage(true)
    })
    const unsubProcessing = elementContext.on(CHECKOUT_MESSAGES.PAYMENT_PROCESSING, () => {
      clearTitle()
      setIsResultPage(true)
    })
    return () => {
      unsubLoaded()
      unsubSuccess()
      unsubProcessing()
    }
  }, [elementContext])

  return { title, isResultPage }
}

export interface BillingCheckoutModalProps {
  elementContext: BillingElements
  open: boolean
  onOpenChange: (open: boolean) => void
  brand?: BillingCheckoutBrand
  isLoaded?: boolean
}

export function BillingCheckoutModal({
  elementContext,
  open,
  onOpenChange,
  brand,
  isLoaded = true,
}: BillingCheckoutModalProps) {
  const { title: heading, isResultPage } = useCheckoutState(elementContext)

  const brandComponent = brand ? BRAND_COMPONENTS[brand] : null
  const shouldShowHeader = !isResultPage
  const handleOpenChange = isResultPage ? () => undefined : onOpenChange

  const checkoutWrapperClassName = cn(styles.modalWrapper, !isLoaded && styles.modalWrapperHidden)

  return (
    <Modal size="md" open={open} onOpenChange={handleOpenChange}>
      <Modal.Content className={styles.modalLoaded}>
        {shouldShowHeader && (
          <>
            <Modal.IconClose />
            <header className={styles.modalHeader}>
              <div className={styles.headerRow}>
                {brandComponent}
                {heading && <Modal.HeaderTitle>{heading}</Modal.HeaderTitle>}
              </div>
            </header>
          </>
        )}
        <Modal.Body className={styles.modalBody} disablePadding>
          {!isLoaded && (
            <div className={styles.loadingContainer}>
              <Spinner size="lg" />
            </div>
          )}
          <BillingCheckoutInline
            elementContext={elementContext}
            className={checkoutWrapperClassName}
          />
        </Modal.Body>
      </Modal.Content>
    </Modal>
  )
}
